/**
 * product-form.js
 *
 * A single web component - <product-form> - owns every add-to-cart call the
 * product page makes: /cart/clear.js, /cart/add.js and the cart section
 * re-render. It never navigates to /checkout on a Kaching page and it never
 * touches window.fetch.
 *
 * -- Kaching Bundles flow ----------------------------------------------
 * The bundle templates (templates/product.bundle-sub-v16.json and siblings)
 * render the Kaching `@app` block - kaching_bundles_app_block - next to the
 * buy_buttons block inside <product-info> (sections/main-product.liquid), and
 * bundle-v21-perfect-widget pairs one widget with one form per .kb-wrap
 * (snippets/kb-buy-buttons.liquid). Kaching binds its own add-to-cart to that
 * form, so when a widget is present the component does NOT add anything
 * itself; it only opens the door for Kaching:
 *
 *   1. click on .product-form__submit is caught in the capture phase
 *   2. POST /cart/clear.js   - once per page load, so the bundle replaces the
 *                              cart instead of stacking on leftover items
 *   3. the click is replayed - Kaching's built-in add-to-cart runs untouched,
 *                              and Kaching's own "Skip cart and go to checkout
 *                              directly" setting owns the checkout handoff
 *
 * Step 3 is the end of the theme's involvement. The cart belongs to Kaching
 * from that point: no second clear, no forced navigation, no fetch wrapper.
 * Shopify ticket 69580807 traced the intermittent bounce back to the homepage
 * to exactly that - a cart clear that landed while checkout was loading handed
 * checkout an empty cart - so CartClearLatch below makes a late clear
 * impossible rather than merely unlikely.
 *
 * With no Kaching widget bound to the form the component behaves like the
 * stock Dawn product form (steps 2-3 are skipped entirely).
 *
 * Per-form opt-out, set on the <product-form> element:
 *   data-kaching-clear-cart="false" - never clear the cart before Kaching
 */

(function () {
  'use strict';

  const SELECTORS = {
    submit: '.product-form__submit',
    spinner: '.loading-overlay__spinner',
    errorWrapper: '.product-form__error-message-wrapper',
    errorMessage: '.product-form__error-message',
    soldOutMessage: '.sold-out-message',
    kachingWidget: 'kaching-bundles-block, kaching-bundle',
    kachingWrap: '.kb-wrap',
    productInfo: 'product-info, .product__info-container',
    activeSellingPlan: '.js_variant_change.active[data-plan-id]',
    activeFreeGift: '.single_gift.active[free-gift-var-id]',
    subscriptionAtc: '.atc_subscription',
    bundleButton: '.js_add_bundle',
  };

  /* Legacy non-Kaching funnels: the theme adds the bundle itself and finishes
     at /checkout. Kaching pages are never in this list - there the app owns the
     checkout handoff. */
  const CHECKOUT_BODY_CLASSES = [
    'bundle-v2',
    'bundle-v4',
    'bundle-v5',
    'bundle-v6',
    'bundle-v7',
    'bundle-v8',
    'bundle-subscription',
    'offer',
    'bundle-sub-2step',
  ];

  const CART_CLEAR_URL = '/cart/clear.js';
  const TOO_MANY_REQUESTS = 429;
  /* How long the button stays busy after the click is handed to Kaching, if
     neither a checkout navigation nor the cart drawer takes over first. */
  const HANDOFF_GRACE = 4000;

  const THROTTLE_MESSAGE =
    'Too many requests &mdash; <button type="button" onclick="location.href=\'/cart/clear?return_to=\'+encodeURIComponent(location.pathname+location.search)" style="text-decoration:underline;background:none;border:none;padding:0;cursor:pointer;font:inherit;color:inherit;">reload the page</button> and try again.';

  const cartAddUrl = () => (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';

  const onBody = (classNames) => classNames.some((name) => document.body.classList.contains(name));

  /* PUB_SUB_EVENTS / publish come from constants.js + pubsub.js and live in the
     global lexical scope, not on window - hence the typeof guards. A section
     that loaded this file without them simply gets no events. */
  const emit = (eventName, payload) => {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS[eventName], payload);
  };

  /**
   * The cart may be cleared at most once per page load, before the first add,
   * and never again afterwards. A clear that lands while checkout is loading
   * hands checkout an empty cart and the customer is returned to the storefront
   * (Shopify ticket 69580807) - so once the click is handed to Kaching, or a
   * checkout navigation starts, the latch is closed for good and an in-flight
   * clear is aborted. Shared by every form on the page because they share one
   * cart: the Subscribe and One-time tabs must not clear behind each other.
   */
  const CartClearLatch = {
    used: false,
    closed: false,
    controller: null,

    /** No clear from here on; abort one that is still in flight. */
    close() {
      this.closed = true;
      if (this.controller) this.controller.abort();
      this.controller = null;
    },

    async run() {
      if (this.used || this.closed) return { cleared: false, status: 0 };
      this.used = true;

      this.controller = typeof AbortController === 'function' ? new AbortController() : null;

      try {
        const response = await fetch(CART_CLEAR_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          signal: this.controller ? this.controller.signal : undefined,
        });
        return { cleared: response.ok, status: response.status };
      } catch (error) {
        /* Aborted, offline or blocked - the add is still worth attempting. */
        return { cleared: false, status: 0 };
      } finally {
        this.controller = null;
      }
    },
  };

  /* Leaving the page means a checkout handoff may be under way. */
  window.addEventListener('pagehide', () => CartClearLatch.close());

  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (!this.setUp) this.setUpOnce();
      if (!this.form) return;

      /* Capture phase on document is the only point that reliably runs before
         the Kaching script's own handler on this button. Re-adding the same
         listener reference is a no-op, so a re-connect cannot double-bind. */
      document.addEventListener('click', this.onDocumentClick, true);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
    }

    setUpOnce() {
      this.setUp = true;
      this.error = false;
      this.busy = false;
      this.kachingBusy = false;
      this.replayingClick = false;

      this.form = this.querySelector('form');
      if (!this.form) return;

      const variantInput = this.form.querySelector('[name=id]');
      if (variantInput) variantInput.disabled = false;

      this.submitButton = this.querySelector('[type="submit"]');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.hideErrors = this.dataset.hideErrors === 'true';

      if (this.submitButton && document.querySelector('cart-drawer')) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      this.onSubmit = this.onSubmit.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
    }

    /* -- Kaching Bundles ----------------------------------------------- */

    /** The Kaching widget bound to this form, or null when there is none. */
    get kachingWidget() {
      const scope = this.closest(SELECTORS.kachingWrap) || this.closest(SELECTORS.productInfo);
      if (!scope) return null;

      const widget = scope.querySelector(SELECTORS.kachingWidget);
      /* A widget inside a hidden .kb-wrap belongs to the other purchase tab. */
      return widget && !widget.closest('[hidden]') ? widget : null;
    }

    get boundToKaching() {
      return Boolean(this.kachingWidget);
    }

    get clearsCartBeforeAdd() {
      if (this.dataset.kachingClearCart === 'false') return false;
      if (CartClearLatch.used || CartClearLatch.closed) return false;
      return this.boundToKaching;
    }

    onDocumentClick(event) {
      if (this.replayingClick) return;

      const button = event.target.closest(SELECTORS.submit);
      if (!button || !this.contains(button)) return;
      if (!this.boundToKaching) return; /* stock theme form, the submit handler has it */

      /* A clear is in flight - swallow the click rather than let Kaching add on
         top of a cart that is about to be emptied. */
      if (this.kachingBusy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      /* Nothing to do in front of Kaching once the cart has been cleared: the
         click goes straight through to the app, which owns the cart and the
         checkout handoff from that point on. */
      if (!this.clearsCartBeforeAdd) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.runKachingFlow(button);
    }

    /** Clear the cart once, then hand the click back to Kaching for good. */
    async runKachingFlow(button) {
      this.kachingBusy = true;
      this.setErrorMessage();
      this.toggleLoading(true);

      try {
        const cleared = await CartClearLatch.run();
        if (cleared.status === TOO_MANY_REQUESTS) {
          this.showThrottleError();
          this.endKachingFlow();
          return;
        }

        /* Closed before the replay, not after: from the moment Kaching has the
           click it may add and navigate to checkout at any time, and no clear
           of ours is allowed to overtake that navigation. */
        CartClearLatch.close();

        const cartWasOpen = this.cartIsOpen();
        this.replayClick(button);

        /* Kaching either navigates to checkout - the button should stay busy
           through the navigation - or opens the cart. releaseAfterHandoff()
           waits for the cart to open, with a grace period as the backstop. */
        this.releaseAfterHandoff(cartWasOpen);
      } catch (error) {
        /* Never leave the button stuck behind a swallowed click. */
        console.error(error);
        this.endKachingFlow();
      }
    }

    cartIsOpen() {
      return Boolean(this.cart && (this.cart.classList.contains('active') || this.cart.querySelector('.active')));
    }

    releaseAfterHandoff(cartWasOpen) {
      /* Kaching may open the cart synchronously inside the replayed click, so
         check before setting up the observer. */
      if (!cartWasOpen && this.cartIsOpen()) {
        this.endKachingFlow();
        return;
      }

      let released = false;

      const release = () => {
        if (released) return;
        released = true;
        observer.disconnect();
        window.clearTimeout(timer);
        this.endKachingFlow();
      };

      const observer = new MutationObserver(() => {
        if (this.cartIsOpen()) release();
      });
      if (this.cart) observer.observe(this.cart, { attributes: true, subtree: true, attributeFilter: ['class'] });

      const timer = window.setTimeout(release, HANDOFF_GRACE);
    }

    endKachingFlow() {
      this.kachingBusy = false;
      if (!this.busy) this.toggleLoading(false);
    }

    replayClick(button) {
      this.replayingClick = true;
      try {
        button.click();
      } finally {
        this.replayingClick = false;
      }
    }

    /* -- Theme add-to-cart --------------------------------------------- */

    async onSubmit(event) {
      event.preventDefault();
      if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.setErrorMessage();
      this.setBusy(true);

      const formData = this.buildFormData();

      try {
        this.handleCartResponse(await this.addToCart(formData), formData);
      } catch (error) {
        if (error && error.throttled) this.showThrottleError();
        else console.error(error);
      } finally {
        this.setBusy(false);
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      }
    }

    buildFormData() {
      const formData = new FormData(this.form);

      if (this.cart) {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      const sellingPlan = document.querySelector(SELECTORS.activeSellingPlan);
      if (sellingPlan) formData.append('selling_plan', sellingPlan.dataset.planId);

      return formData;
    }

    async addToCart(formData) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type']; /* let the browser set the multipart boundary */
      config.body = formData;

      const response = await fetch(cartAddUrl(), config);
      if (response.status === TOO_MANY_REQUESTS) {
        throw Object.assign(new Error('Cart API rate limited'), { throttled: true });
      }

      return response.json();
    }

    handleCartResponse(response, formData) {
      /* The Cart API only returns a `status` when the add failed. */
      if (response.status) {
        this.handleAddError(response, formData);
        return;
      }

      if (!this.cart) {
        window.location = cartUrl();
        return;
      }

      this.error = false;
      emit('cartUpdate', {
        source: 'product-form',
        productVariantId: formData.get('id'),
        cartData: response,
      });

      const quickAddModal = this.closest('quick-add-modal');
      if (quickAddModal) {
        document.body.addEventListener('modalClosed', () => setTimeout(() => this.cart.renderContents(response)), {
          once: true,
        });
        quickAddModal.hide(true);
        return;
      }

      if (onBody(CHECKOUT_BODY_CLASSES)) {
        this.goToCheckout();
        return;
      }

      this.cart.renderContents(response);
    }

    handleAddError(response, formData) {
      this.error = true;
      emit('cartError', {
        source: 'product-form',
        productVariantId: formData.get('id'),
        errors: response.errors || response.description,
        message: response.message,
      });
      this.setErrorMessage(response.description);

      const soldOutMessage = this.submitButton.querySelector(SELECTORS.soldOutMessage);
      if (!soldOutMessage) return;

      this.submitButton.setAttribute('aria-disabled', 'true');
      const label = this.submitButton.querySelector('span');
      if (label) label.classList.add('hidden');
      soldOutMessage.classList.remove('hidden');
    }

    /**
     * Legacy non-Kaching funnels only (CHECKOUT_BODY_CLASSES): the theme did
     * the add itself and carries the free gifts over as a discount code. The
     * latch is closed first so nothing can clear the cart mid-navigation.
     */
    goToCheckout() {
      CartClearLatch.close();

      let discount = '';

      if (document.querySelector(SELECTORS.subscriptionAtc)) {
        const freeGifts = document.querySelectorAll(SELECTORS.activeFreeGift).length;
        if (freeGifts === 2) discount = '?discount=GET2FREE';
        else if (freeGifts === 1) discount = '?discount=GET1FREE';
      }

      const bundleButton = document.querySelector(SELECTORS.bundleButton);
      if (bundleButton) bundleButton.classList.remove('loading');

      window.location.href = '/checkout' + discount;
    }

    /* -- Button + error state ------------------------------------------ */

    setBusy(isBusy) {
      this.busy = isBusy;
      if (!this.submitButton) return;

      if (isBusy) this.submitButton.setAttribute('aria-disabled', 'true');
      else if (!this.error) this.submitButton.removeAttribute('aria-disabled');

      this.toggleLoading(isBusy);
    }

    toggleLoading(isLoading) {
      if (!this.submitButton) return;

      this.submitButton.classList.toggle('loading', isLoading);
      const spinner = this.querySelector(SELECTORS.spinner);
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    get errorWrapper() {
      if (!this.cachedErrorWrapper) this.cachedErrorWrapper = this.querySelector(SELECTORS.errorWrapper);
      return this.cachedErrorWrapper;
    }

    setErrorMessage(message = '') {
      if (this.hideErrors) return;

      const wrapper = this.errorWrapper;
      if (!wrapper) return;

      wrapper.toggleAttribute('hidden', !message);
      wrapper.style.color = '';

      const target = wrapper.querySelector(SELECTORS.errorMessage);
      if (target) target.textContent = message;
    }

    showThrottleError() {
      const wrapper = this.errorWrapper;
      if (!wrapper) return;

      const target = wrapper.querySelector(SELECTORS.errorMessage);
      if (target) target.innerHTML = THROTTLE_MESSAGE;

      wrapper.removeAttribute('hidden');
      wrapper.style.color = '#cc0000';
      this.error = true;
    }
  }

  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
})();
